/**
 * F-099 / F-050 (slice 7) — Part 1 of a product on the Modify Existing Product form, pre-filled
 * from the PIM record the same way D&D's intake seed builds it (API `DdPimPart`): "Product
 * Sourcing" → the sourcing preset (and the Sales-side vended mark), "Stock #1" → the substrate
 * (a catalog pick when the PIM text matches one, else the "keep current" option), Width /
 * Height → the first number, Single/Double → sides, pieces 1, no modifiers. Sales changes what
 * is different, adds parts, picks modifiers — all optional (owner 2026-10-07).
 */
import { SUBSTRATES } from '@/data/mockSignBuilderCatalog'
import type { WorkflowPimProduct } from '@/lib/sales-workflow/workflowPimProduct'
import {
  MODIFY_PART_CURRENT_SUBSTRATE,
  createEmptyModifyPartDraft,
  type ModifyPartDraft
} from '@/types/salesWorkflow'

export type PimPartSourcing =
  | 'manufactured_in_house'
  | 'hardware_in_house'
  | 'hardware_drop_shipped'
  | 'vended_drop_shipped'

/** PIM "Product Sourcing" value (normalised) → part sourcing preset — mirror of DdPimPart::SOURCING_MAP. */
const SOURCING_MAP: Record<string, PimPartSourcing> = {
  'manufactured in house': 'manufactured_in_house',
  'manufactured in house or buy vended drop shipped': 'manufactured_in_house',
  'hardware in house': 'hardware_in_house',
  'hardware drop shipped': 'hardware_drop_shipped',
  'vended in house finish': 'vended_drop_shipped',
  'vended drop shipped': 'vended_drop_shipped'
}

function normaliseSourcing(value: string): string {
  return value
    .toLowerCase()
    .replace(/[–—\-/]/g, ' ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** The first PIM sourcing value the presets know; Manufactured – In House when none. */
export function sourcingFromPim(
  raw: readonly string[] | string | null | undefined
): PimPartSourcing {
  const values = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : []
  for (const value of values) {
    if (typeof value !== 'string') continue
    const key = SOURCING_MAP[normaliseSourcing(value)]
    if (key) return key
  }
  return 'manufactured_in_house'
}

/** Only Manufactured – In House parts carry a stock and dimensions (Candace #5). */
export function sourcingHasBuild(sourcing: string): boolean {
  return sourcing === 'manufactured_in_house'
}

/** "24.5 in" → "24.5"; nothing / 0 → "". */
export function pimDimensionText(raw: unknown): string {
  let n: number | null = null
  if (typeof raw === 'number') n = raw
  else if (typeof raw === 'string') {
    const m = /(\d+(?:\.\d+)?)/.exec(raw)
    n = m ? parseFloat(m[1]!) : null
  }
  if (n === null || !Number.isFinite(n) || n <= 0) return ''
  return String(n)
}

/** PIM "Single-Sided / Double-Sided Print" → the form's single / double (single when unknown). */
export function pimSidesValue(raw: unknown): 'single' | 'double' {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  return v.startsWith('double') ? 'double' : 'single'
}

/** A PIM stock text that names a catalog substrate (by PIQ code or label) → its catalog id. */
export function catalogSubstrateIdForPimStock(stock: string): string | null {
  const needle = stock.trim().toLowerCase()
  if (needle === '') return null
  const hit = SUBSTRATES.find(
    (s) =>
      s.piq.toLowerCase() === needle ||
      s.label.toLowerCase() === needle ||
      s.id.toLowerCase() === needle
  )
  return hit ? hit.id : null
}

export function productPartsKey(product: Pick<WorkflowPimProduct, 'id' | 'sku'>): string {
  const id = (product.id ?? '').trim()
  return id !== '' ? id : product.sku.trim()
}

/** Part 1 of a product as the Modify form opens it — from the PIM record (DdPimPart mirror). */
export function pimPartDraft(product: WorkflowPimProduct): ModifyPartDraft {
  const sourcing = sourcingFromPim(product.productSourcing)
  const hasBuild = sourcingHasBuild(sourcing)
  const stock = (product.base_substrate ?? '').trim()
  const catalogId = hasBuild ? catalogSubstrateIdForPimStock(stock) : null
  return createEmptyModifyPartDraft({
    label: '',
    vended: !hasBuild,
    sourcing,
    substrate: hasBuild && stock !== '' ? (catalogId ?? MODIFY_PART_CURRENT_SUBSTRATE) : '',
    current_substrate: hasBuild ? stock : '',
    width: hasBuild ? pimDimensionText(product.width) : '',
    height: hasBuild ? pimDimensionText(product.height) : '',
    pieces: '1',
    single_double: pimSidesValue(product.single_double),
    notes: '',
    modifiers: []
  })
}
